import type Phaser from "phaser";
import { CANVAS_COLOURS, type WorldRect } from "daedalus-core";

/**
 * The boxes an editor draws where its atlas has no picture for what the document holds (ADR-129 decision 3.6): a
 * prop pulled or a pack added in another tab, until this tab's own Save and recompile. Each box is an outline
 * with a keyline and one diagonal, at a fixed screen width at any zoom. One `Graphics` object over the map,
 * redrawn only when the boxes or the zoom change.
 */

/**
 * The outline's width in screen pixels.
 */
export const MISSING_FRAME_LINE = 1.5;

/**
 * The keyline's width in screen pixels, drawn under the outline so it holds on light ground.
 */
export const MISSING_FRAME_KEYLINE = 3.5;

/**
 * The box of one cell.
 *
 * @param cx Cell x in tiles.
 * @param cy Cell y in tiles.
 * @param tileSize Tile size in pixels.
 * @returns The cell's rectangle in world pixels.
 */
export function cellBox(cx: number, cy: number, tileSize: number): WorldRect {
  return { left: cx * tileSize, top: cy * tileSize, right: (cx + 1) * tileSize, bottom: (cy + 1) * tileSize };
}

/**
 * The boxes of one scene.
 */
export class MissingFrameView {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private version = -1;
  private zoom = 0;
  private drawn = 0;

  /**
   * @param scene Scene to draw into.
   * @param depth Depth over the map's layers.
   */
  public constructor(scene: Phaser.Scene, depth: number) {
    this.graphics = scene.add.graphics().setDepth(depth);
  }

  /**
   * How many boxes the last draw held.
   *
   * @returns The count.
   */
  public get count(): number {
    return this.drawn;
  }

  /**
   * Draws the boxes, or clears them, when their version or the zoom changed since the last call.
   *
   * @param version A number the caller changes whenever the boxes change.
   * @param boxes The boxes, read only when a redraw happens.
   * @param zoom The camera's zoom.
   * @returns True when a redraw happened; with no box now and none drawn before, nothing is redrawn.
   */
  public sync(version: number, boxes: () => Iterable<WorldRect>, zoom: number): boolean {
    if (version === this.version && zoom === this.zoom) {
      return false;
    }
    this.version = version;
    this.zoom = zoom;
    const list = [...boxes()];
    if (list.length === 0 && this.drawn === 0) {
      return false;
    }
    const g = this.graphics;
    g.clear();
    this.drawn = list.length;
    const { missingFrame, missingFrameOutline } = CANVAS_COLOURS;
    g.lineStyle(MISSING_FRAME_KEYLINE / zoom, missingFrameOutline.rgb, missingFrameOutline.alpha);
    for (const box of list) {
      g.strokeRect(box.left, box.top, box.right - box.left, box.bottom - box.top);
    }
    g.lineStyle(MISSING_FRAME_LINE / zoom, missingFrame.rgb, missingFrame.alpha);
    for (const box of list) {
      g.strokeRect(box.left, box.top, box.right - box.left, box.bottom - box.top);
      g.lineBetween(box.left, box.bottom, box.right, box.top);
    }
    return true;
  }

  /**
   * Removes the drawing.
   */
  public destroy(): void {
    this.graphics.destroy();
  }
}
